import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useInspectionForms, type FormSummary } from '../../../api/forms';
import type { Inspection } from '../../../api/inspections';
import { useEnumLabel } from '../../../api/enums';
import { Table, Tag } from '../../../components/idsk';
import { formatDateTime } from '../../../lib/format';
import { QueryError } from '../../common/QueryError';
import { Loading } from '../../common/LoadingPage';

export function CompletenessTag({ form }: { form: Pick<FormSummary, 'exists' | 'complete'> }) {
  const { t } = useTranslation();
  if (!form.exists) return <Tag colour="inactive">{t('forms.notStarted')}</Tag>;
  return form.complete ? <Tag colour="green">{t('forms.completeTag')}</Tag> : <Tag colour="yellow">{t('forms.incompleteTag')}</Tag>;
}

/** The four status forms of an inspection. */
export function InspectionFormsTab({ inspection }: { inspection: Inspection }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const forms = useInspectionForms(inspection.id);
  if (forms.error) return <QueryError error={forms.error} />;
  if (forms.isPending) return <Loading />;
  return (
    <Table<FormSummary>
      caption={t('forms.caption')}
      rows={forms.data}
      rowKey={(f) => f.formType}
      columns={[
        {
          key: 'form',
          header: t('forms.form'),
          rowHeader: true,
          render: (f) => (
            <Link className="govuk-link" to={`/inspekcie/${inspection.id}/formulare/${f.formType}`}>
              {label('formTypes', f.formType)}
            </Link>
          ),
        },
        { key: 'phase', header: t('forms.phase'), render: (f) => t(`forms.phases.${f.phase}`) },
        { key: 'complete', header: t('forms.completeness'), render: (f) => <CompletenessTag form={f} /> },
        { key: 'version', header: t('forms.templateVersion'), render: (f) => t('templates.versionN', { n: f.templateVersionNo }) },
        { key: 'custom', header: t('forms.customFields'), numeric: true, render: (f) => f.customFieldCount },
        { key: 'modified', header: t('common.updatedAt'), render: (f) => (f.lastModifiedAt ? `${formatDateTime(f.lastModifiedAt)} (${f.lastModifiedBy})` : '–') },
      ]}
    />
  );
}
